import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, frameNote, isEmpty, sum } from '../frame';
import { compact } from '../format';
import { animation, itemTooltip, legendFor, SURFACE_GAP } from '../options';

/**
 * Composition of a whole — and only that.
 *
 * A ring answers "roughly what share?" at a glance. It cannot answer "which of
 * these two is bigger?" when the two are close, because comparing arc angles is
 * something people are bad at. If the reader's question is comparison, the bar
 * chart is the right form and this one is the wrong one.
 *
 * Six segments is the cap. Past that, adjacent arcs are too thin to read and the
 * tail folds into "Other" — never into a seventh and eighth colour.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MAX_SLICES = 6;

/**
 * `hole` is the difference between a donut and a pie: a donut keeps the centre
 * open (for the total readout), a pie fills to the middle. Both cap at six
 * slices and fold the tail into "Other" — the arc-angle problem is the same.
 */
const shareView = (data: ChartData, theme: ReturnType<typeof useTheme>, hole: boolean, fields?: ChartFields) => {
  const frame = buildFrame(data, { limit: 500, ...fields });
  if (isEmpty(frame)) return null;

  const values = frame.series[0].values;
  // Deliberately NOT re-sorted by value. Slot 1 goes to the first category the
  // query returned, slot 2 to the second, and so on — so colour follows the
  // entity. Ranking the slices here instead would repaint every survivor the
  // moment a filter changed the order, and a reader who learned "iOS is
  // indigo" would be misled. The catalogue queries already ORDER BY.
  const present = frame.categories
    .map((name, i) => ({ name, value: values[i] ?? 0 }))
    .filter((slice) => slice.value > 0);

  if (!present.length) return null;

  const head = present.slice(0, MAX_SLICES - 1);
  const tail = present.slice(MAX_SLICES - 1);
  const slices =
    tail.length > 1
      ? [...head, { name: `Other (${tail.length})`, value: sum(tail.map((s) => s.value)) }]
      : present.slice(0, MAX_SLICES);

  const total = sum(slices.map((s) => s.value));

  const option: EChartsOption = {
    ...animation,
    legend: legendFor(theme, slices.length),
    tooltip: itemTooltip(theme, { showPercent: true }),
    series: [
      {
        type: 'pie' as const,
        name: frame.measureName || 'Share',
        // A pie fills to the centre; a donut leaves the hole for the total.
        radius: hole ? ['58%', '82%'] : ['0%', '82%'],
        center: ['50%', '46%'],
        avoidLabelOverlap: true,
        data: slices.map((slice, i) => ({
          ...slice,
          itemStyle: {
            color: seriesColor(theme, i),
            // Slices touch, so the surface colour does the separating —
            // never an outline drawn around the mark.
            borderColor: theme.surface,
            borderWidth: SURFACE_GAP,
          },
        })),
        // The donut's open centre carries the total; a pie has no room for it,
        // so it labels each slice with its percentage instead.
        label: hole
          ? {
              show: true,
              position: 'center' as const,
              formatter: () => `{value|${compact(total)}}\n{label|Total}`,
              rich: {
                value: {
                  color: theme.ink,
                  fontSize: 22,
                  fontWeight: 600,
                  fontFamily: theme.font,
                  lineHeight: 28,
                },
                label: { color: theme.inkMuted, fontSize: 11, fontFamily: theme.font },
              },
            }
          : {
              show: true,
              color: theme.inkSecondary,
              fontSize: 11,
              fontFamily: theme.font,
              formatter: '{d}%',
            },
        emphasis: hole ? { label: { show: true }, scaleSize: 4 } : { scaleSize: 4 },
        labelLine: hole ? { show: false } : { show: true, length: 6, length2: 6 },
      },
    ],
  };

  return { option, note: frameNote(frame) };
};

export const DonutChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => shareView(data, theme, true, fields), [data, theme, fields]);

  if (!view) return <Empty message="No positive values to chart" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Donut chart" />
    </Plot>
  );
};

export const PieChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => shareView(data, theme, false, fields), [data, theme, fields]);

  if (!view) return <Empty message="No positive values to chart" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Pie chart" />
    </Plot>
  );
};
